import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PdfColor } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { PiccoloColors } from '../../pdf/board/logico';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/label';

/**
 * Pastelky (Colored Pencils) - Realistic pencil-shaped cards with images
 *
 * Layout: Portrait pages, 5 pencils per page.
 * Each pencil: graphite tip → wood → colored section → ferrule → body → eraser.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pastelky
 */
export class Pastelky implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const inflateMargin = PdfUnit.FromMillimeters(2);
    const { origin, size } = calcOrigin(1, 5, margin, PdfSize.A4);

    const eraserLen = PdfUnit.FromMillimeters(10);
    const ferruleLen = PdfUnit.FromMillimeters(4);
    const graphiteLen = PdfUnit.FromMillimeters(3);
    const woodLen = PdfUnit.FromMillimeters(6);

    const estimatedContentWidth =
      size.width - 2 * margin - size.height - eraserLen - ferruleLen - 2 * inflateMargin;
    const estimatedContentHeight = size.height - 2 * margin - 2 * inflateMargin;
    const halfRectSize = new PdfSize(estimatedContentWidth / 2, estimatedContentHeight);

    const allTexts = opts.list.flatMap((item) => {
      const texts: string[][] = [];
      const leftText = item.left.getText();
      if (leftText) texts.push([leftText]);
      const rightText = item.right.getText();
      if (rightText) texts.push([rightText]);
      return texts;
    });

    const pageCount = Math.ceil(opts.list.length / 5);
    const pages: ReturnType<typeof pdf.addPage>[] = [];
    for (let i = 0; i < pageCount; i++) pages.push(pdf.addPage());

    const font =
      allTexts.length > 0
        ? pages[0].adjustFontMulti(allTexts, GUtil.baseFont, halfRectSize)
        : GUtil.baseFont;

    const graphiteColor = PdfColor.fromHex('#2F2F2F');
    const woodColor = PdfColor.fromHex('#DEB887');
    const ferruleColor = PdfColor.fromHex('#C0C0C0');
    const eraserColor = PdfColor.fromHex('#FFB6C1');

    for (let i = 0; i < opts.list.length; i++) {
      const pageIdx = Math.floor(i / 5);
      const page = pages[pageIdx];
      const pos = i % 5;

      const rect = new PdfRect(origin.x, origin.y + pos * size.height, size.width, size.height);

      const tipX = rect.x + margin;
      const centerY = rect.y + rect.height / 2;
      const bodyStartX = rect.x + margin + rect.height;
      const bodyEndX = rect.right - margin;
      const pencilTop = rect.y + margin;
      const pencilBottom = rect.bottom - margin;
      const pencilHeight = pencilBottom - pencilTop;

      const points: PdfPoint[] = [
        new PdfPoint(tipX, centerY),
        new PdfPoint(bodyStartX, pencilTop),
        new PdfPoint(bodyEndX, pencilTop),
        new PdfPoint(bodyEndX, pencilBottom),
        new PdfPoint(bodyStartX, pencilBottom),
      ];

      const coloredEndX = tipX + rect.height / 2;
      const woodEndX = tipX + graphiteLen + woodLen;
      const eraserStartX = bodyEndX - eraserLen;
      const ferruleEndX = bodyStartX + ferruleLen;

      const pencilOutlineY = (x: number, isTop: boolean): number => {
        if (x <= tipX) return centerY;
        if (x <= bodyStartX) {
          const ratio = (x - tipX) / (bodyStartX - tipX);
          return isTop
            ? centerY - (pencilHeight / 2) * ratio
            : centerY + (pencilHeight / 2) * ratio;
        }
        return isTop ? pencilTop : pencilBottom;
      };

      // Body background (white)
      page.drawRectangle(
        null,
        PdfBrush.White,
        new PdfRect(ferruleEndX, pencilTop, eraserStartX - ferruleEndX, pencilHeight)
      );

      // Eraser (pink rounded rectangle)
      page.drawRoundedRectangle(
        null,
        new PdfBrush(eraserColor),
        new PdfRect(eraserStartX, pencilTop, eraserLen, pencilHeight),
        PdfUnit.FromMillimeters(2)
      );

      // Ferrule (silver band)
      page.drawRectangle(
        null,
        new PdfBrush(ferruleColor),
        new PdfRect(bodyStartX, pencilTop, ferruleLen, pencilHeight)
      );

      // Colored section (trapezoid)
      const coloredPoints: PdfPoint[] = [
        new PdfPoint(woodEndX, pencilOutlineY(woodEndX, true)),
        new PdfPoint(woodEndX, pencilOutlineY(woodEndX, false)),
        new PdfPoint(coloredEndX, pencilOutlineY(coloredEndX, false)),
        new PdfPoint(coloredEndX, pencilOutlineY(coloredEndX, true)),
      ];
      page.drawPolygon(null, new PdfBrush(PiccoloColors[pos]), coloredPoints);

      // Wood section (trapezoid)
      const woodPoints: PdfPoint[] = [
        new PdfPoint(tipX + graphiteLen, pencilOutlineY(tipX + graphiteLen, true)),
        new PdfPoint(tipX + graphiteLen, pencilOutlineY(tipX + graphiteLen, false)),
        new PdfPoint(woodEndX, pencilOutlineY(woodEndX, false)),
        new PdfPoint(woodEndX, pencilOutlineY(woodEndX, true)),
      ];
      page.drawPolygon(null, new PdfBrush(woodColor), woodPoints);

      // Graphite point (small dark triangle)
      const graphitePoints: PdfPoint[] = [
        new PdfPoint(tipX, centerY),
        new PdfPoint(tipX + graphiteLen, pencilOutlineY(tipX + graphiteLen, false)),
        new PdfPoint(tipX + graphiteLen, pencilOutlineY(tipX + graphiteLen, true)),
      ];
      page.drawPolygon(null, new PdfBrush(graphiteColor), graphitePoints);

      // Content area (two halves between ferrule and eraser)
      const contentStartX = ferruleEndX + inflateMargin;
      const contentEndX = eraserStartX - inflateMargin;
      const contentWidth = contentEndX - contentStartX;
      const contentTop = pencilTop + inflateMargin;
      const contentBottom = pencilBottom - inflateMargin;
      const halfWidth = contentWidth / 2;

      const leftRect = new PdfRect(
        contentStartX,
        contentTop,
        halfWidth,
        contentBottom - contentTop
      );
      page.drawLabel(opts.list[i].left, { rect: leftRect, font });

      const rightRect = new PdfRect(
        contentStartX + halfWidth,
        contentTop,
        halfWidth,
        contentBottom - contentTop
      );
      page.drawLabel(opts.list[i].right, { rect: rightRect, font });

      // Pencil outline
      page.drawPolygon(GUtil.basePen, null, points);

      // Cut line
      page.drawRectangle(GUtil.cutPen, null, rect);
    }
  }
}
